import React from 'react';

/**
 * Whether a visibility/focus event should trigger a refetch: only for an enabled resource
 * on a foregrounded tab. Pure, so the guard is unit-testable without a DOM.
 */
export const shouldRefetchOnVisible = ({ enabled, visible }: { enabled: boolean; visible: boolean }): boolean =>
  enabled && visible;

/**
 * Refetch a resource when the tab returns to the foreground: whenever the document becomes visible again (tab
 * switch) or the window regains focus (window/app switch), never for a backgrounded tab or while disabled.
 * Used by the affiliate reads (campaigns created in the external console in another tab) and by the app-wide
 * creative-jobs stream (a background tab's websocket can be throttled or suspended, so a run's progress is
 * re-read on return).
 *
 * `reload` is read through a ref so a changing identity never re-subscribes. Both events can fire for one return,
 * so a caller whose reload is not idempotent-cheap should debounce it.
 */
export const useRefetchOnVisible = (reload: () => void, enabled: boolean): void => {
  const reloadRef = React.useRef(reload);
  reloadRef.current = reload;

  React.useEffect(() => {
    if (!enabled) return undefined;
    const onVisible = (): void => {
      if (shouldRefetchOnVisible({ enabled, visible: document.visibilityState === 'visible' })) {
        reloadRef.current();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [enabled]);
};

export default useRefetchOnVisible;
